/* eslint-disable react/no-unstable-nested-components */
import useRootIssuer from "components/company/v3/context/hooks/useRootIssuer";
import FinancialsChart from "components/financials/modern/FinancialsChart";
import FinancialsComparison from "components/financials/modern/FinancialsComparison";
import FinancialsTable from "components/financials/modern/FinancialsTable";
import type { IFinancialsTemplateFieldsFragment } from "components/financials/modern/graphql/fragments/__generated__/FinancialsTemplateFieldsFragment.generated";
import type { IIndicatorFieldsFragment } from "components/financials/modern/graphql/fragments/__generated__/IndicatorFieldsFragment.generated";
import type { FinancialsTab } from "components/financials/modern/props";
import getPlatformVerticalByIssuer, {
  overrideVerticalForDataSource,
} from "components/financials/modern/utils/platformVertical";
import type { SelectedCompanyIssuer } from "components/issuers/IssuerByTickerSymbolSearch";
import useChangeSetting from "components/settings/hooks/useChangeSetting";
import ContentContainerBox from "components/stocks/ContentContainerBox";
import classNames from "components/ui/classNames";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "components/ui/Tabs";
import useSelfie from "lib/hooks/useSelfie";
import { ActivityIcon, ScaleIcon, Table2Icon } from "lucide-react";
import { useState } from "react";
import { FormattedMessage } from "react-intl";
import type { IIndicatorDataSource, ISecurityCountry } from "types/graphqlTypes";
import { IFinancialsStatementType } from "types/graphqlTypes";

interface FinancialsProps {
  tab: FinancialsTab;
  tickerSymbol: string;
  country: ISecurityCountry;
  indicatorDataSource: IIndicatorDataSource;
  currency?: string;
  statementType?: IFinancialsStatementType;
  interim?: string | null;
  reportDates?: [Date | null, Date | null];
  templateId?: string | null;
  indicators?: IIndicatorFieldsFragment[];
  adversaries?: SelectedCompanyIssuer[];
  limit?: number;

  canChangeDataSource?: boolean;

  onChangeIndicatorDataSource: (dataSource: IIndicatorDataSource) => void;
  onChangeStatementType: (statementType: IFinancialsStatementType) => void;
  onChangeInterim: (interim: string | null) => void;
  onChangeCurrency: (currency: string) => void;
  onChangeReportDates: (reportDates: [Date | null, Date | null]) => void;
  onChangeTemplate: (template: IFinancialsTemplateFieldsFragment | null) => void;
  onChangeTab: (tab: FinancialsTab) => void;
  onChangeIndicators: (indicators: IIndicatorFieldsFragment[]) => void;
  onChangeAdversaries: (adversaries: SelectedCompanyIssuer[]) => void;
  onChangeLimit: (limit: number) => void;
}

export default function Financials({
  tab,
  tickerSymbol,
  country,
  indicatorDataSource,
  currency = "USD",
  statementType,
  interim,
  reportDates,
  templateId,
  indicators,
  adversaries,
  limit,

  canChangeDataSource,

  onChangeStatementType,
  onChangeInterim,
  onChangeIndicatorDataSource,
  onChangeCurrency,
  onChangeReportDates,
  onChangeTemplate,
  onChangeTab,
  onChangeIndicators,
  onChangeAdversaries,
  onChangeLimit,
}: FinancialsProps) {
  const viewer = useSelfie();
  const issuer = useRootIssuer();
  const { value: financialsTableSettings, set: changeFinancialsTableSettings } = useChangeSetting("financialsTable", {
    sortDirection: "ASC",
    textAlignment: "CENTER",
    fullBleed: false,
  });

  const [arbitraryIndicators, setArbitraryIndicators] = useState<IIndicatorFieldsFragment[]>([]);
  const [selectedIndicators, setSelectedIndicators] = useState<IIndicatorFieldsFragment[]>([]);

  return (
    <ContentContainerBox className="p-0 dark:border-none sm:px-0">
      <Tabs
        className="w-full space-y-0 rounded-xl font-normal"
        value={tab || "TABLE"}
        defaultValue={tab || "TABLE"}
        onValueChange={(tab) => onChangeTab?.(tab as FinancialsTab)}
      >
        <TabsList
          className={classNames(
            "flex w-full flex-1 divide-x overflow-hidden rounded-b-none rounded-t-xl p-0 dark:divide-slate-950",
            "bg-slate-50",
            "dark:border-slate-900 dark:bg-slate-800",
          )}
        >
          <TabsTrigger
            value="TABLE"
            className={classNames(
              "w-full justify-center overflow-hidden rounded-none border-b border-b-transparent px-2 md:justify-start",
              "border-b-slate-200 dark:border-b-slate-950",
              "group",
            )}
          >
            <div className="flex flex-col items-center gap-2 truncate px-1.5 py-1 text-left md:flex-row">
              <div
                className={classNames(
                  "flex items-center justify-center rounded-full p-2 squircle",
                  "bg-slate-50 dark:bg-indigo-300/10 dark:text-indigo-200",
                  "group-data-[state=active]:dark:bg-indigo-300/20 group-data-[state=active]:dark:text-indigo-100",
                  "group-data-[state=active]:bg-indigo-50/50 group-data-[state=active]:text-indigo-600",
                )}
              >
                <Table2Icon className="inline-flex" size={16} strokeWidth={2} />
              </div>
              <span
                className={classNames(
                  "leading-6",
                  "text-slate-500 group-data-[state=active]:font-bold",
                  "group-data-[state=active]:text-slate-600 group-data-[state=active]:dark:text-slate-200",
                )}
              >
                <FormattedMessage id="wauBuH" defaultMessage="Balanços" />
              </span>
            </div>
          </TabsTrigger>

          {viewer?.isPremium && (
            <TabsTrigger
              value="CHART"
              disabled={!viewer?.isPremium}
              className={classNames(
                "w-full justify-center overflow-hidden rounded-none border-b border-b-transparent px-2 md:justify-start",
                "border-b-slate-200 dark:border-b-slate-950",
                "group",
              )}
            >
              <div className="flex flex-col items-center gap-2 truncate px-1.5 py-1 text-left md:flex-row">
                <div
                  className={classNames(
                    "flex items-center justify-center rounded-full p-2 squircle",
                    "bg-slate-50 dark:bg-teal-300/10 dark:text-teal-200",
                    "group-data-[state=active]:dark:bg-teal-300/20 group-data-[state=active]:dark:text-teal-200",
                    "group-data-[state=active]:bg-teal-50/50 group-data-[state=active]:text-teal-600",
                  )}
                >
                  <ActivityIcon className="inline-flex" size={16} strokeWidth={2} />
                </div>
                <span
                  className={classNames(
                    "leading-6",
                    "text-slate-500 group-data-[state=active]:font-bold",
                    "group-data-[state=active]:text-slate-600 group-data-[state=active]:dark:text-slate-200",
                  )}
                >
                  <FormattedMessage id="NUM22U" defaultMessage="Gráfico" />
                </span>
              </div>
            </TabsTrigger>
          )}

          {viewer?.isPremium && (
            <TabsTrigger
              value="COMPARE"
              className={classNames(
                "w-full justify-center overflow-hidden rounded-none border-b border-b-transparent px-2 md:justify-start",
                "border-b-slate-200 dark:border-b-slate-950",
                "group",
              )}
            >
              <div className="flex flex-col items-center gap-2 truncate px-1.5 py-1 text-left md:flex-row">
                <div
                  className={classNames(
                    "flex items-center justify-center rounded-full p-2 squircle",
                    "bg-slate-50 dark:bg-orange-300/10 dark:text-orange-200",
                    "group-data-[state=active]:dark:bg-orange-300/20 group-data-[state=active]:dark:text-orange-200",
                    "group-data-[state=active]:bg-orange-50/50 group-data-[state=active]:text-orange-600",
                  )}
                >
                  <ScaleIcon className="inline-flex" size={16} strokeWidth={2} />
                </div>
                <span
                  className={classNames(
                    "leading-6",
                    "text-slate-500 group-data-[state=active]:font-bold",
                    "group-data-[state=active]:text-slate-600 group-data-[state=active]:dark:text-slate-200",
                  )}
                >
                  <FormattedMessage id="QNZcX0" defaultMessage="Comparativo" />
                </span>
              </div>
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent className="space-y-4 border-none p-4" value="TABLE">
          <FinancialsTable
            vertical={overrideVerticalForDataSource(getPlatformVerticalByIssuer(issuer), indicatorDataSource)}
            tickerSymbol={tickerSymbol}
            country={country}
            currency={currency}
            indicatorDataSource={indicatorDataSource}
            canChangeDataSource={canChangeDataSource}
            statementType={statementType || IFinancialsStatementType.Annual}
            interim={interim}
            reportDates={reportDates}
            templateId={templateId}
            arbitraryIndicators={arbitraryIndicators}
            textAlignment={financialsTableSettings?.textAlignment || "CENTER"}
            sortDirection={financialsTableSettings?.sortDirection || "ASC"}
            onChangeArbitraryIndicators={setArbitraryIndicators}
            onChangeIndicatorDataSource={onChangeIndicatorDataSource}
            onChangeStatementType={onChangeStatementType}
            onChangeInterim={onChangeInterim}
            onChangeCurrency={onChangeCurrency}
            onChangeReportDates={onChangeReportDates}
            onChangeTemplate={onChangeTemplate}
            onChangeTextAlignment={(textAlignment) => {
              changeFinancialsTableSettings((current) => ({
                ...current,
                textAlignment,
              }));
            }}
            onChangeSortDirection={(sortDirection) => {
              changeFinancialsTableSettings((current) => ({
                ...current,
                sortDirection,
              }));
            }}
          />
        </TabsContent>

        <TabsContent className="space-y-4 border-none dark:p-4" value="CHART">
          <FinancialsChart
            vertical={overrideVerticalForDataSource(getPlatformVerticalByIssuer(issuer), indicatorDataSource)}
            tickerSymbol={tickerSymbol}
            country={country}
            currency={currency}
            indicatorDataSource={indicatorDataSource}
            statementType={statementType || IFinancialsStatementType.Annual}
            interim={interim}
            arbitraryIndicators={arbitraryIndicators}
            selectedIndicators={selectedIndicators}
            templateId={templateId}
            onChangeStatementType={onChangeStatementType}
            onChangeInterim={onChangeInterim}
            onChangeSelectedIndicators={setSelectedIndicators}
            onChangeArbitraryIndicators={setArbitraryIndicators}
          />
        </TabsContent>

        <TabsContent className="space-y-4 border-none dark:p-4" value="COMPARE">
          <FinancialsComparison
            tickerSymbol={tickerSymbol}
            country={country}
            currency={currency}
            vertical={getPlatformVerticalByIssuer(issuer)}
            indicators={indicators}
            arbitraryIndicators={arbitraryIndicators}
            indicatorDataSource={indicatorDataSource}
            onChangeIndicatorDataSource={onChangeIndicatorDataSource}
            canChangeDataSource={canChangeDataSource}
            statementType={statementType || IFinancialsStatementType.Annual}
            interim={interim}
            limit={limit}
            selectedIssuers={adversaries || []}
            onChangeStatementType={onChangeStatementType}
            onChangeInterim={onChangeInterim}
            onChangeIndicators={onChangeIndicators}
            onChangeAdversaries={onChangeAdversaries}
            onChangeLimit={onChangeLimit}
          />
        </TabsContent>
      </Tabs>
    </ContentContainerBox>
  );
}
